import React, { useRef, useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "../CallDetails/CallDetails.module.css";
import Loader2 from "../Loader2/Loader2";
import DetailModal from "../DetailModal/DetailModal";
import { useDashboardStore } from "../../Store/agentZustandStore";
import { getAgentCallById, getAgentById } from "../../Store/apiStore";
import { DateTime } from "luxon";
const CallDetails = () => {
  const location = useLocation();   //adding json call history
  // In-app navigation passes agentId + start_timestamp via location.state. A cold
  // lifecycle/email deep-link (/call-details/<callId>?agentId=…&start_timestamp=…)
  // has NO state, so fall back to the URL query. Additive — warm in-app nav
  // (location.state) still wins and is unchanged.
  const _q = new URLSearchParams(location.search);
  const agentId = location.state?.agentId || _q.get("agentId");
  const start_timestamp = location.state?.start_timestamp || _q.get("start_timestamp");
  const [isChatModalOpen, setChatModalOpen] = useState(false);
  const [callData, setCallData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [visibleCount, setVisibleCount] = useState(0);
  const [messagesPerReveal, setMessagesPerReveal] = useState(0);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timezone, setTimezone] = useState("")
  const [agentInfo, setAgentInfo] = useState(null); // attending agent fetched by id
  const agentData = JSON.parse(
    sessionStorage.getItem("dashboard-session-storage")
  );
  const { agents, } = useDashboardStore();
  const { callId } = useParams();
  // const agents = agentData?.state?.agents || [];
  const navigate = useNavigate();

  // Per-agent call-log gating (multi-owner). A team member may open a call only
  // for an agent they're allowed to see logs for: admin for that agent's owner,
  // or granted viewCallLogs. We resolve the permission from THIS call's agent
  // (via the tagged dashboard store), not a global owner — so a legitimately
  // permitted call is never wrongly blocked. Unknown agent → don't block (the
  // list already filters; avoids false redirects on cold deep-links).
  const isTeamMemberCD = !!localStorage.getItem("teamOwnerId");
  const dashAgentsCD = agentData?.state?.agents || [];
  const thisCallAgent = dashAgentsCD.find((a) => a.agent_id === agentId);
  const canViewThisCall =
    !isTeamMemberCD ||
    thisCallAgent?.__role === "admin" ||
    thisCallAgent?.__role === "owner" || // your own agent
    !!thisCallAgent?.__permissions?.viewCallLogs;
  useEffect(() => {
    if (isTeamMemberCD && agentId && thisCallAgent && !canViewThisCall) {
      navigate("/dashboard", { replace: true });
    }
  }, [isTeamMemberCD, agentId, thisCallAgent, canViewThisCall, navigate]);
  const [audioProgress, setAudioProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Back handler that stays coherent on a cold WebView deep-link. Warm in-app
  // nav (came from the call list) carries location.state → plain history back
  // returns to that screen, unchanged. A cold lifecycle/email deep-link has NO
  // in-app history, so navigate(-1) drops the user onto the native shell's
  // initial screen (e.g. personal details) with no agent context — instead send
  // them to the agent's home screen WITH agentId so the WebView stays scoped.
  const handleBack = () => {
    if (location.state?.agentId) {
      navigate(-1); // warm: return to the screen they came from (unchanged)
    } else if (agentId) {
      navigate(`/fast-agent-detail?agentId=${agentId}`, { replace: true }); // cold deep-link
    } else {
      navigate(-1); // no agentId at all — best effort
    }
  };

  // Hardware/gesture back on a COLD deep-link — NATIVE WebView ONLY. Plain web
  // browsers are left completely alone (they keep their normal browser back), so
  // nothing about the web flow changes. Inside the native shell (android/App.tsx
  // handleBackPress) Android hardware back is forwarded to webView.goBack() only
  // when canGoBack is true, else an Exit prompt shows. On a cold arrival the page
  // loaded via location.replace(), so a raw back can follow stale web history to
  // the shell's personal-details screen. Seed ONE trap entry: it makes
  // canGoBack=true (so native forwards the back into the WebView) and lets us
  // catch that back as a popstate and redirect to the agent screen. Warm in-app
  // nav (location.state present) is skipped too — its back stays unchanged.
  useEffect(() => {
    // Gate 1: only inside the RN WebView (bridge object present). Web browser → no-op.
    if (typeof window === "undefined" || !window.ReactNativeWebView) return;
    // Gate 2: only a cold deep-link (no in-app state) that carries an agentId.
    if (location.state?.agentId || !agentId) return;
    window.history.pushState(null, "", window.location.href);
    const onPop = () => navigate(`/fast-agent-detail?agentId=${agentId}`, { replace: true });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const toggleAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };
  const handleAudioProgress = () => {
    const audio = audioRef.current;
    if (audio) {
      const progress = (audio.currentTime / audio.duration) * 100;
      setAudioProgress(progress);
      setCurrentTime(audio.currentTime);
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    const seekTime = (event.target.value / 100) * audio.duration;
    audio.currentTime = seekTime;
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""
      }${seconds}`;
  };

  useEffect(() => {
    if (!callId || !agentId || !start_timestamp) return;

    const fetchCallDetails = async () => {
      try {
        // const res = await axios.get(
        //   `https://api.retellai.com/v2/get-call/${callId}`,
        //   {
        //     headers: {
        //       Authorization: `Bearer ${process.env.REACT_APP_API_RETELL_API}`,
        //     },
        //   }
        // );
        const res = await getAgentCallById(agentId, callId, start_timestamp)
        setCallData(res?.call);
        setTimezone(res?.timezone || "")
      } catch (err) {
        console.error("Error fetching call details:", err);
        setError("Could not load call details.");
      } finally {
        setLoading(false);
      }
    };

    fetchCallDetails();
  }, [callId]);

  useEffect(() => {
    if (!agentId) return;
    let active = true;
    (async () => {
      try {
        const res = await getAgentById(agentId);
        if (active) setAgentInfo(res?.data || res?.agent || null);
      } catch (err) {
        console.error("Error fetching agent details:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [agentId]);

  useEffect(() => {
    if (callData && callData.transcript_object) {
      const transcriptLength = callData.transcript_object.length;
      setMessagesPerReveal(Math.ceil(transcriptLength / 4));
    }
  }, [callData]);

  useEffect(() => {
    setVisibleCount(messagesPerReveal);
  }, [messagesPerReveal]);
  //time
 // call start time
const time = callData?.start_timestamp || start_timestamp || "";
const date = callData?.start_timestamp || start_timestamp || "";

  //getTimeFromTimestamp
 const getTimeFromTimestamp = () => {
  if (!time) return "-";

  return DateTime
    .fromMillis(Number(time))
    .setZone(timezone || "UTC")
    .toFormat("hh:mm a"); 
};

  //getDateFromTimestamp
 const getDateFromTimestamp = () => {
  if (!date) return "-";

  return DateTime
    .fromMillis(Number(date))
    .setZone(timezone || "UTC")
    .toFormat("yyyy-MM-dd");
};

  if (!callId) return <p>No call selected.</p>;
  if (loading) return <Loader2 />;
  if (error) return <p>{error}</p>;

  const transcript = callData.chat_type == "api_chat" ? callData.message_with_tool_calls : callData.transcript_object || [];
  const formattedDate = new Date(callData?.end_timestamp)?.toLocaleDateString(
    "en-GB",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );
  const formattedTime = new Date(callData?.end_timestamp)?.toLocaleTimeString(
    "en-GB",
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }
  );
  // console.log('callData',callData)
  let data = callData.call_analysis?.custom_analysis_data;
  let name = data?.name || "";

  let lead_type = data?.lead_type || "";

  // Phone number resolution, call-type aware.
  // phone_call → "Phone number" is the real telephony leg (from_number) and the
  // number the agent collected on the call goes to "Other Number".
  // web_call / chat → no telephony leg, so custom_analysis_data.phone_number is
  // the primary, falling back to the retell dynamic variables.
  const isPhoneCall = callData?.call_type === "phone_call";
  const analysisPhone = data?.phone_number || "";
  const dyn = callData?.retell_llm_dynamic_variables || {};
  const dynamicPhone = dyn.user_phone || dyn.customer_phone || "";
  // Compare on the last 10 digits so "+12102795554" and "(210) 279-5554" match.
  const phoneKey = (v) => String(v || "").replace(/\D/g, "").slice(-10);
  // Outbound me from_number business ka apna number hai — lead to_number pe hai
  // (Pounce lead_phone bhi bhejta hai).
  const isOutbound =
    String(callData?.direction || "").toLowerCase() === "outbound" || !!dyn.lead_phone;
  const leadLeg = isOutbound ? callData?.to_number || dyn.lead_phone : callData?.from_number;
  const primaryPhone = isPhoneCall
    ? leadLeg || analysisPhone || dynamicPhone
    : analysisPhone || dynamicPhone;
  // Hide the second row when it repeats the primary or is empty.
  const otherPhone =
    analysisPhone && phoneKey(analysisPhone) !== phoneKey(primaryPhone)
      ? analysisPhone
      : "";

  const convertMsToMinSec = (durationMs) => {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes} Min ${seconds} Sec`;
  };
  function formatName(name) {
    if (!name) return "";

    if (name.includes(" ")) {
      const firstName = name.split(" ")[0];
      if (firstName.length <= 7) {
        return firstName;
      } else {
        return firstName.substring(0, 10) + "...";
      }
    } else {
      if (name.length > 7) {
        return name.substring(0, 10) + "...";
      }
      return name;
    }
  }
const currentAgent =
    agents.find((a) => a.agent_id === callData?.agent_id) ||
    callData?.agent ||
    agentInfo ||
    null;
  const attendedByName = formatName(currentAgent?.agentName) || "Unknown Agent";
  const rawAvatar = currentAgent?.avatar;
  const agentAvatar = rawAvatar
    ? /^(https?:|\/)/.test(rawAvatar)
      ? rawAvatar
      : `/${rawAvatar}`
    : "/images/agent_dp.png";
  return (
    <div className={styles.CallDetailsMain}>
      <div className={styles.forSticky}>
        <header className={styles.header}>
          <div className={styles.profileBack}>
            <svg
              onClick={handleBack}
              style={{ cursor: "pointer" }}
              width="50"
              height="50"
              viewBox="0 0 50 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="50" height="50" rx="25" fill="#F9F9F9" />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M21.293 25.7071C20.9025 25.3166 20.9025 24.6834 21.293 24.2929L26.9499 18.636C27.3404 18.2455 27.9736 18.2455 28.3641 18.636C28.7546 19.0266 28.7546 19.6597 28.3641 20.0503L23.4144 25L28.3641 29.9497C28.7546 30.3403 28.7546 30.9734 28.3641 31.364C27.9736 31.7545 27.3404 31.7545 26.9499 31.364L21.293 25.7071Z"
                fill="#0A0A0A"
                fill-opacity="0.9"
              />
            </svg>

            <h4 className={styles.headerTitle}>Call Details</h4>
          </div>
        </header>
        <div className={styles.agentCard}>
          <div className={styles.details}>
            <p>Caller Name</p>
            <p>Sentiment</p>
          </div>
          <div className={styles.detail2}>
            <div className={styles.userName}>
              <h2>{name || callData?.retell_llm_dynamic_variables?.user_name|| "Unknown"}</h2>
            </div>
            <div
              className={`${styles.status} ${callData.call_analysis?.user_sentiment === "Positive"
                ? styles.green
                : callData.call_analysis?.user_sentiment === "Neutral"
                  ? styles.yellow
                  : styles.red
                }`}
            >
              <p>{callData.call_analysis?.user_sentiment || "N/A"}</p>
            </div>
          </div>
          <hr className={styles.hrline} />
          <div className={styles.details3}>
            <div className={styles.Part1}>
              <p>{getTimeFromTimestamp()}</p>
              <strong>{getDateFromTimestamp()}</strong>
            </div>
            <div className={styles.Part2}>
              <p>Attended by</p>
              <strong>
                {callData?.agent_id ? attendedByName : "Thinking..."}
              </strong>
            </div>

            <div className={styles.Part3}>
              <p>Durations</p>

              <strong>{callData.chat_type == "api_chat" ? callData?.duration_ms || 0 : convertMsToMinSec(callData.duration_ms)}</strong>
            </div>
          </div>
        </div>
      </div>

      <section>
        <div className={styles.DataMain}>
          <div className={styles.dataTitle}>
            <h2>Data Collected</h2>
          </div>
          <div className={styles.dataCard}>
            {/* old — not call-type aware, and `data.from_number` never exists on
                custom_analysis_data (from_number is top-level on the call):
            <div className={styles.PhoneNumber}>
              <p>Phone number</p>
              <b>
                {data.from_number||data?.phone_number
                  || callData?.retell_llm_dynamic_variables?.user_phone
                  || callData?.retell_llm_dynamic_variables?.customer_phone||"-"}
              </b>

            </div>
            */}
            <div className={styles.PhoneNumber}>
              <p>Phone number</p>
              <b>{primaryPhone || "-"}</b>
            </div>
            {otherPhone && (
              <div className={styles.PhoneNumber}>
                <p>Other Number</p>
                <b>{otherPhone}</b>
              </div>
            )}
            <div className={styles.EmailAddress}>
              <p>Email address</p>
              <b>{data?.email || callData?.retell_llm_dynamic_variables?.user_email || '-'}</b>
            </div>
            <div className={styles.Address}>
              <p>Address (if collected)</p>
              <b>{data?.address ? data?.address : '-'}</b>
            </div>
            <div className={styles.Reason}>
              <p>Reason</p>
              <b>{data?.reason ? data?.reason : '-'}</b>
            </div>
          </div>
          <div className={styles.moredetailsDiv}>
            <div className={styles.dataTitle}>
              <h2>More Details</h2>
            </div>
            <div></div>
            <div className={styles.channel}>
              <p className={styles.Ptext}>Channel</p>
              <div className={styles.PhoneDiv}>
                <p>


                  {callData?.call_type === "phone_call"
                    ?callData?.call_type
                    : callData?.call_type === "api_chat"
                      ? "chat"
                      : callData?.chat_type === "api_chat"
                        ? "chat"
                        : callData?.call_type}

                  {" "}

                </p>
              </div>
            </div>
            <div className={styles.channel}>
              <p className={styles.Ptext}>Lead Type</p>
              <div>
                <strong>{lead_type || "Unknown"}</strong>
              </div>
            </div>
            {callData.chat_type == "api_chat" || !callData.recording_url ? "" : <div className={styles.channel}>
              <p className={styles.Ptext}>Call Recording</p>
              <div className={styles.audioPlayer}>
                <div onClick={toggleAudio} className={styles.playPauseBtn}>
                  {isPlaying ? (
                    // Pause Icon
                    <svg
                      width="30"
                      height="30"
                      viewBox="0 0 50 50"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="25" cy="25" r="25" fill="#EBE2FF" />
                      <rect
                        x="18"
                        y="17"
                        width="5"
                        height="18"
                        rx="2"
                        fill="#6524EB"
                      />
                      <rect
                        x="27"
                        y="17"
                        width="5"
                        height="18"
                        rx="2"
                        fill="#6524EB"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="30"
                      height="30"
                      viewBox="0 0 50 50"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="25" cy="25" r="25" fill="#EBE2FF" />
                      <polygon points="20,17 35,25 20,33" fill="#6524EB" />
                    </svg>
                  )}
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={audioProgress}
                  onChange={handleSeek}
                  className={styles.audioSeek}
                />
                <div className={styles.audioTime}>
                  <span>{formatTime(currentTime)}</span> /{" "}
                  <span>{formatTime(audioRef.current?.duration || 0)}</span>
                </div>
                <audio
                  ref={audioRef}
                  src={callData.recording_url}
                  onEnded={() => setIsPlaying(false)}
                  onTimeUpdate={handleAudioProgress}
                />
              </div>
            </div>}
          </div>
          <div className={styles.summaryDiv}>
            <div className={styles.dataTitle}>
              <h2>{callData.chat_type == "api_chat" ? "Chat summary" : "Call summary"}</h2>
            </div>
            <p className={styles.Ptext}>
              {callData.chat_type == "api_chat" ? callData.call_analysis?.chat_summary : callData.call_analysis?.call_summary || "No data"}
            </p>
          </div>
          {callData?.call_analysis?.custom_analysis_data?.message_taken && (
            <div className={styles.summaryDiv} style={{ borderLeft: "4px solid #2E7D32", background: "#F1F8E9", borderRadius: 8, padding: "14px 16px", margin: "0 16px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#2E7D32", background: "#E8F5E9", padding: "2px 8px", borderRadius: 4 }}>Message Taken</span>
                {callData.call_analysis.custom_analysis_data.name && (
                  <span style={{ fontSize: 12, color: "#555" }}>from {callData.call_analysis.custom_analysis_data.name}</span>
                )}
              </div>
              <p style={{ fontSize: 14, color: "#1B5E20", lineHeight: 1.5, margin: 0 }}>
                {callData.call_analysis.custom_analysis_data.message_text || callData.call_analysis.custom_analysis_data.reason || "Message recorded"}
              </p>
              {callData.call_analysis.custom_analysis_data.urgency === "critical" && (
                <span style={{ display: "inline-block", marginTop: 6, fontSize: 11, fontWeight: 700, color: "#C62828", background: "#FFEBEE", padding: "2px 8px", borderRadius: 4 }}>URGENT</span>
              )}
            </div>
          )}
          {transcript && transcript.length > 0 && (
          <div className={styles.summaryDiv}>
            <div className={styles.dataTitle}>
              <h2> {callData.chat_type == "api_chat" ? "Chat Transcript" : "Call Transcript"}</h2>
            </div>
            <div className={styles.ChatBox}>
              {transcript.find((msg) => msg.role === "agent") && (
                <div className={styles.messageRow}>
                  <div className={styles.profile}>
                    <img
                      src={agentAvatar}
                      alt="Agent"
                      className={styles.profileImage}
                      onError={(e) => {
                        e.currentTarget.src = "/images/agent_dp.png";
                      }}
                    />
                  </div>

                  <div className={styles.messageLeft}>
                    <div className={styles.bubbleLeft}>
                      {transcript.find((msg) => msg.role === "agent").content}
                    </div>
                    <span className={styles.time}>
                      {callData?.agent_id ? attendedByName : "Thinking..."}
                    </span>
                  </div>
                </div>
              )}

              {transcript.find((msg) => msg.role === "user") && (
                <div className={styles.messageRight}>
                  <div className={styles.bubbleRight}>
                    {transcript.find((msg) => msg.role === "user").content}
                  </div>
                  <span className={styles.time}>User</span>
                </div>
              )}

              {transcript && transcript.length > 0 && (
                <div
                  className={styles.chatBtn}
                  onClick={() => setChatModalOpen(true)}
                >
                  <p>Show Full Transcript</p>
                </div>
              )}
            </div>

            <DetailModal
              isOpen={isChatModalOpen}
              onClose={() => setChatModalOpen(false)}
              height="80dvh"
            >
              <div className={styles.titlediv}>
                <h1>Call Transcript</h1>
              </div>
              <div className={styles.ChatBox2}>
                {transcript.map((entry, index) => (
                  entry.role === "agent" ? (
                    <div key={index} className={styles.messageWrapper}>
                      <div className={styles.messageLeftWrapper}>
                        <img
                          src={agentAvatar}
                          alt="Agent"
                          className={styles.profileImage}
                          onError={(e) => {
                            e.currentTarget.src = "/images/agent_dp.png";
                          }}
                        />
                        <div className={styles.messageLeft}>
                          <div className={styles.bubbleLeft}>
                            {entry.content}
                          </div>
                          <span className={styles.time}>
                            {callData?.agent_id ? attendedByName : "Thinking..."}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={index} className={styles.messageWrapper2}>
                      <div className={styles.messageRight}>
                        <div className={styles.bubbleRight}>
                          {entry.content}
                        </div>
                      </div>
                      <span className={styles.time}>User</span>
                    </div>
                  )
                ))}
              </div>
            </DetailModal>
          </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CallDetails;
